import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import VitalsMonitorForm from "../VitalsMonitorChart/VitalsMonitorForm";

import { useDispatch, useSelector } from "react-redux";
import { fetchVitalsChart, updateVitalsChart } from "../../../store/slices/vitalsSlice";

const EditVitalsMonitor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { isLoading } = useSelector(state => state.vitalsChart || {});
    const { user } = useSelector(state => state.auth);
    const { state } = useLocation();
    const { caseId, patientId } = state || {};


    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await dispatch(fetchVitalsChart(id)).unwrap();

                // Map backend snake_case to frontend camelCase
                const mappedData = {
                    patientId: patientId || response.patientId,
                    caseId: caseId || response.caseId,
                    name: response.name || "",
                    dob: response.dob || "",
                    documentName: response.document_name || "Vitals Monitoring Chart",
                    effectiveDate: response.effective_date || "",
                    approvedBy: response.approved_by || "",
                    approvedDate: response.approved_date || "",
                    nextRevisionBy: response.next_revision_by || "",
                    nextRevisionDate: response.next_revision_date || "",
                    vitalsRecords: (Array.isArray(response.vital_records) ? response.vital_records.slice() : [])
                        .sort((a, b) => {
                            const aTime = a.date_time ? new Date(a.date_time).getTime() : (a.created_at ? new Date(a.created_at).getTime() : a.id);
                            const bTime = b.date_time ? new Date(b.date_time).getTime() : (b.created_at ? new Date(b.created_at).getTime() : b.id);
                            return bTime - aTime;
                        })
                        .map((r) => ({
                            id: r.id,
                            dateTime: r.date_time ? r.date_time.replace(" ", "T") : "",
                            bp: r.bp || "",
                            pulse: r.pulse || "",
                            resp: r.resp || "",
                            temp: r.temp || "",
                            rbs: r.rbs || "",
                            fbs: r.fbs || "",
                            weight: r.weight || "",
                            remarks: r.remarks || "",
                            nurse: r.nurse || "",
                            created_at: r.created_at || "",
                        })),
                };

                setFormData(mappedData);
            } catch (err) {
                Swal.fire("Error", "Failed to fetch Vitals Monitoring Chart data.", "error");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required.";
        if (!formData.dob) newErrors.dob = "DOB is required.";
        if (!formData.vitalsRecords || formData.vitalsRecords.length === 0) {
            newErrors.vitalsRecords = "At least one vital record is required.";
        } else {
            formData.vitalsRecords.forEach((r, i) => {
                if (!r.dateTime) newErrors[`vitalRecord_${i}_dateTime`] = `Date & Time is required for row ${i + 1}`;
            });
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before updating.",
                icon: "warning",
            });
            return;
        }

        try {
            // Map frontend camelCase to backend snake_case
            const payload = {
                patient_id: formData.patientId,
                case_id: formData.caseId,
                name: formData.name,
                dob: formData.dob,
                document_name: formData.documentName,
                effective_date: formData.effectiveDate,
                approved_by: formData.approvedBy,
                approved_date: formData.approvedDate,
                next_revision_by: formData.nextRevisionBy,
                next_revision_date: formData.nextRevisionDate,
                vital_records: formData.vitalsRecords.map((r) => ({
                    id: r.id,
                    date_time: r.dateTime ? r.dateTime.replace("T", " ") : "", // convert to "YYYY-MM-DD HH:mm"
                    bp: r.bp,
                    pulse: r.pulse,
                    resp: r.resp,
                    temp: r.temp,
                    rbs: r.rbs,
                    fbs: r.fbs,
                    weight: r.weight,
                    remarks: r.remarks,
                    nurse: r.nurse,
                })),
            };


            await dispatch(updateVitalsChart({ case_id: caseId, chartData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Vitals Monitoring Chart updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            Swal.fire("Error", err || "Failed to update Vitals Monitoring Chart.", "error");
        }
    };

    if (loading) return <div className="text-center mt-5">Loading...</div>;
    if (!formData) return <div className="text-center mt-5">No data found.</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/vitals-monitor/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors)
                                    .filter(Boolean)
                                    .map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <VitalsMonitorForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 sticky-form-submit"
                                        disabled={isLoading}
                                    >
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditVitalsMonitor;
